import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Modal,
  Box,
  TextField,
  MenuItem,
  Checkbox,
  Typography,
  Popover,
  Autocomplete,
} from "@mui/material";
import axios from "axios";
import {
  Edit,
  Delete,
  Print,
  Visibility,
  Add,
  Close,
  Download,
} from "@mui/icons-material";
import { fetchPaymentDetailsByYearMonth } from "../../../Services/Services";
import { generatePaymetPdf } from "../Accounts/PaymentPrint";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const PaymentList = () => {
  const currentYear = new Date().getFullYear();

  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(null);
  const [data, setData] = useState([]);
  const [PaymentDetails, setPaymentDetails] = useState([]);
  // Years list (dynamic)
  const years = Array.from(
    { length: currentYear - 2025 + 1 },
    (_, i) => 2025 + i
  );

  const months = [
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  useEffect(() => {
    const loadData = async () => {
      if (year && month) {
        try {
          const res = await fetchPaymentDetailsByYearMonth(year, month.value);
          setPaymentDetails(res);
        } catch (error) {
          console.error("Error fetching payment details:", error);
          setPaymentDetails([]);
        }
      } else {
        setPaymentDetails([]);
      }
    };

    loadData();
  }, [year, month]);

  
  // 🔹 Styled Excel Export Function
  const handleExportExcel = async () => {
    if (!PaymentDetails || PaymentDetails.length === 0) {
      alert("No data available to export");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Payments");

    // 🔹 Define Columns
    worksheet.columns = [
      { header: "S.No", key: "sno" },
      { header: "Payment No", key: "payment_no" },
      { header: "Project", key: "project" },
      { header: "Division", key: "division" },
      { header: "Entry Type", key: "entry_type" },
      { header: "Payment Type", key: "payment_type" },
      { header: "Payment Mode", key: "payment_mode" },
      { header: "Transaction No", key: "transaction_no" },
      { header: "Amount", key: "amount" },
      { header: "Narration", key: "narration" },
      { header: "Vendor", key: "vendor" },
      { header: "Created By", key: "created_by" },
      { header: "Approved By", key: "approved_by" },
      { header: "Remarks", key: "remarks" },
    ];

    // 🔹 Apply Header Style
    worksheet.getRow(1).eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFF00" }, // Yellow header
      };
      cell.font = { bold: true };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
      cell.alignment = { horizontal: "center", vertical: "middle" };
    });

    // 🔹 Add Data Rows
    PaymentDetails.forEach((p, index) => {
      worksheet.addRow({
        sno: index + 1,
        payment_no: p.payment_no,
        project: p.project?.project_name || "-",
        division: p.division?.division || "-",
        entry_type: p.entry_type,
        payment_type: p.payment_type,
        payment_mode: p.payment_mode,
        transaction_no: p.transaction_no,
        amount: p.amount,
        narration: p.narration,
        vendor: p.vendor?.name || "-",
        created_by: p.created_by_name,
        approved_by: p.approved_name,
        remarks: p.remarks,
      });
    });

    // 🔹 Apply Borders + Autofit columns
    worksheet.eachRow((row) => {
      row.eachCell((cell) => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        cell.alignment = { vertical: "middle", wrapText: true };
      });
    });

    worksheet.columns.forEach((column) => {
      let maxLength = 10;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const colLength = cell.value ? cell.value.toString().length : 10;
        if (colLength > maxLength) maxLength = colLength;
      });
      column.width = maxLength + 2; // Auto-fit
    });

    // 🔹 Save Excel File
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `Payments_${year}_${month.value}.xlsx`);
  };

  const userRole = localStorage.getItem("role");
  return (
    <Box p={2}>
      {/* Header with dropdowns */}
      <Box display="flex" gap={2} mb={3} sx={{ p: 0, pt: 0, mt: -4, mb: 1 }}>
        <Autocomplete
          options={years}
          value={year}
          onChange={(e, newValue) => setYear(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Year"
              sx={{
                height: 35, // ✅ search box ke barabar
                minWidth: 120,
                display: "flex",
                alignItems: "center",
                fontSize: "0.7rem",
                borderRadius: "0",
                "& .MuiInputBase-root": {
                  height: 35, // ✅ inner input ki height
                  fontSize: "0.7rem", // ✅ text chhota
                  borderRadius: 0, // ✅ rounded hat gaya
                  padding: "0 6px", // ✅ thoda compact
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem", // ✅ label bhi chhota
                  top: -3, // ✅ label thoda adjust
                },
              }}
            />
          )}
          fullWidth
        />

        <Autocomplete
          options={months}
          getOptionLabel={(option) => option.label}
          value={month}
          onChange={(e, newValue) => setMonth(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Month"
              sx={{
                height: 35, // ✅ search box ke barabar
                minWidth: 120,
                display: "flex",
                alignItems: "center",
                fontSize: "0.7rem",
                // px: 2,
                borderRadius: "0",
                "& .MuiInputBase-root": {
                  height: 35, // ✅ inner input ki height
                  fontSize: "0.7rem", // ✅ text chhota
                  borderRadius: 0, // ✅ rounded hat gaya
                  padding: "0 6px", // ✅ compact
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem", // ✅ label bhi chhota
                  top: -3, // ✅ label thoda adjust
                },
              }}
            />
          )}
          fullWidth
        />

        <IconButton size="small" color="inherit">
          <Download fontSize="small" onClick={handleExportExcel} />
        </IconButton>
      </Box>

      {/* Table - Header always visible */}
      <TableContainer
        component={Paper}
        sx={{ maxHeight: 580, overflow: "auto" }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{
            minWidth: 1600,
            "& td, & th": {
              textAlign: "center",
              whiteSpace: "nowrap",
              backgroundColor: "#f5f5f5",
              borderBottom: "1px solid #ccc",
            },
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: "#f5f5f5",
                "& th": {
                  fontWeight: "bold",
                  color: "#333",
                  borderBottom: "1px solid #999",
                },
              }}
            >
              <TableCell
                sx={{
                  position: "sticky",
                  left: 0,
                  backgroundColor: "#f5f5f5",
                  zIndex: 3,
                  minWidth: 80,
                }}
              >
                S.No.
              </TableCell>

              <TableCell>Payment No</TableCell>
              <TableCell>Project</TableCell>
              <TableCell>Division</TableCell>
              <TableCell>Entry Type</TableCell>
              <TableCell>Payment Type</TableCell>
              <TableCell>Payment Mode</TableCell>
              <TableCell>Transaction No</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Narration</TableCell>

              <TableCell>Vendor</TableCell>
              <TableCell>Created By</TableCell>
              <TableCell>Approved By</TableCell>
              <TableCell>Attachment</TableCell>
              <TableCell>Remarks</TableCell>
              <TableCell
                align="center"
                sx={{
                  position: "sticky",
                  right: 0,
                  backgroundColor: "#f5f5f5",
                  zIndex: 3,
                  minWidth: 180,
                }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {PaymentDetails.map((p, index) => (
              <TableRow
                hover
                key={index}
                sx={{
                  position: "sticky",
                  right: 0,
                  backgroundColor: "#fff",
                  zIndex: 1,

                  backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff", // alternate row color
                  "&:hover": {
                    backgroundColor: "#e3f2fd",
                    "& td": {
                      backgroundColor: "#e3f2fd !important",
                    },
                  },
                  "& td": {
                    paddingTop: "2px !important",
                    paddingBottom: "2px !important",
                    fontSize: { xs: "0.75rem", sm: "0.8125rem" }, // responsive font
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 120,
                    color: (theme) =>
                      theme.palette.mode === "dark" ? "white" : "inherit",
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? "black" : "inherit",
                  },
                }}
              >
                <TableCell
                  sx={{
                    position: "sticky",
                    left: 0,
                    zIndex: 3,
                    minWidth: 80,
                  }}
                >
                  {index + 1}
                </TableCell>

                <TableCell>{p.payment_no}</TableCell>
                <TableCell>{p.project?.project_name || "-"}</TableCell>
                <TableCell>{p.division?.division || "-"}</TableCell>
                <TableCell>{p.entry_type}</TableCell>
                <TableCell>{p.payment_type}</TableCell>
                <TableCell>{p.payment_mode}</TableCell>
                <TableCell>{p.transaction_no}</TableCell>
                <TableCell>{p.amount}</TableCell>
                <TableCell>{p.narration}</TableCell>
                {/* <TableCell>{p.attachment ? p.attachment.name : "-"}</TableCell> */}

                <TableCell>{p.vendor?.name || "-"}</TableCell>
                <TableCell>{p.created_by_name}</TableCell>
                <TableCell>{p.approved_name}</TableCell>
                <TableCell>
                  {typeof p.attachment === "string" ? (
                    <a
                      href={`${p.attachment.replace(/^\//, "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <IconButton
                        sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                        size="small"
                        color="info"
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                    </a>
                  ) : p.attachment instanceof File ? (
                    p.attachment.name
                  ) : (
                    ""
                  )}
                </TableCell>
                <TableCell>{p.remarks}</TableCell>
                <TableCell
                  align="center"
                  sx={{
                    position: "sticky",
                    right: 0,
                    backgroundColor: "#f5f5f5",
                    zIndex: 3,
                    minWidth: 180, // <-- Consistent width
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 0.5,
                    }}
                  >
                    <IconButton
                      sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                      color="warning"
                      size="small"
                      onClick={() => generatePaymetPdf(p)}
                    >
                      <Print fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PaymentList;
